import { useMutation } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';

/** Thin client for the caller lookup in src/api-contacts-identity/owner-lookup.js
 *  — "whose client is this phone number?", searched across the WHOLE directory,
 *  not just the contacts the signed-in user created. */

export interface CallerOwner {
  id: string;
  name: string;
  username: string;
}

export interface CallerMatch {
  id: string;
  name: string;
  company: string;
  /** True when the signed-in user created (owns) this contact. */
  isMine: boolean;
  owner: CallerOwner | null;
}

export interface CallerLookupResult {
  /** The number as the server normalised it. */
  phone: string;
  matches: CallerMatch[];
}

export function useCallerLookup() {
  return useMutation({
    mutationFn: async (phone: string) => {
      const { data } = await api.get<CallerLookupResult>('/api/contacts-identity/owner-lookup', { params: { phone } });
      return data;
    },
  });
}

export function useNotifyContactOwner() {
  return useMutation({
    mutationFn: async ({ contactId, note }: { contactId: string; note: string }) => {
      const { data } = await api.post<{ ok: boolean; notified: CallerOwner }>(
        `/api/contacts-identity/${contactId}/notify-owner`, { note },
      );
      return data;
    },
  });
}

/** The server's own message ("You already notified them…") is written to be shown as-is. */
export function callerLookupError(err: unknown, fallback: string): string {
  const e = err as { response?: { data?: { error?: string } } };
  return e?.response?.data?.error || fallback;
}
